import { ContentType } from '@medplum/core';
import type { MedplumClient } from '@medplum/core';
import type { Identifier } from '@medplum/fhirtypes';

import { config } from '../config';
import {
  FEEDBACK_SCREENSHOT_MIME_TYPES,
  type ProductFeedbackInput,
} from '@unify/fhir-model/schemas/product-feedback.schema';

const PRODUCT_FEEDBACK_BOT: Identifier = {
  system: 'https://unify.health/bots',
  value: 'product-feedback',
};

export type FeedbackEnvironment = ProductFeedbackInput['environment'];

/** LOCAL = local dev server; PROD = production build; DEV = any other deployed build. */
export function resolveFeedbackEnvironment(): FeedbackEnvironment {
  if (import.meta.env.DEV) return 'LOCAL';
  if (config.isProduction) return 'PROD';
  return 'DEV';
}

function getBotTarget(): string | Identifier {
  const id = import.meta.env.VITE_MEDPLUM_PRODUCT_FEEDBACK_BOT_ID?.trim();
  if (id) return id;
  return PRODUCT_FEEDBACK_BOT;
}

export function isFeedbackScreenshotMimeType(
  value: string,
): value is (typeof FEEDBACK_SCREENSHOT_MIME_TYPES)[number] {
  return (FEEDBACK_SCREENSHOT_MIME_TYPES as readonly string[]).includes(value);
}

export async function fileToBase64(file: File): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
  const comma = dataUrl.indexOf(',');
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}

export interface ProductFeedbackBotResult {
  ok: boolean;
}

export async function submitProductFeedback(
  medplum: MedplumClient,
  input: ProductFeedbackInput,
): Promise<ProductFeedbackBotResult> {
  return medplum.executeBot(getBotTarget(), input, ContentType.JSON) as Promise<ProductFeedbackBotResult>;
}
